import { Chip } from "../../components/ui/Chip";
import { CodeGroupedHistory } from "./CodeGroupedHistory";
import { monthName, recordedLabel } from "./format";
import { KIND_LABEL } from "./logTabs";
import type { LogEntry, ResinContext } from "./types";

interface Props {
  month: string;
  resin: ResinContext;
  entries: LogEntry[];
  error: string | null;
  selected: { kind: LogEntry["kind"]; id: number } | null;
  onSelect: (entry: LogEntry) => void;
}

export function LogHistory({ month, resin, entries, error, selected, onSelect }: Props) {
  const { resinFamilyCode } = resin;
  const count = entries.length;

  return (
    <details className="card mt-6">
      <summary className="cursor-pointer text-[15px] text-white">
        {resinFamilyCode} · {monthName(month)} logging history · {count} {count === 1 ? "record" : "records"}
      </summary>
      {error ? (
        <div className="empty mt-4">
          <span className="material-symbols-sharp">error</span>Couldn't load the history: {error}
        </div>
      ) : count === 0 ? (
        <p className="mt-4 text-rs-light-gray text-[13px]">
          No {resinFamilyCode} records saved for this month yet.
        </p>
      ) : (
        <div className="mt-4">
          <CodeGroupedHistory
            items={entries}
            resin={resin}
            noun="record"
            isCompact={false}
            recordedUtcOf={(entry) => entry.recordedUtc}
            renderItems={(groupEntries) => (
              <ul className="flex flex-col gap-1.5 list-none p-0 m-0">
                {groupEntries.map((entry) => {
                  const isSelected = selected?.kind === entry.kind && selected?.id === entry.id;
                  return (
                    <li key={`${entry.kind}-${entry.id}`}>
                      <button
                        type="button"
                        className={`w-full grid grid-cols-[130px_1fr_auto] items-center gap-4 text-left bg-transparent border rounded-md px-3.5 py-2.5 hover:border-rs-green ${
                          isSelected ? "border-rs-green" : "border-rs-jet"
                        }`}
                        onClick={() => onSelect(entry)}
                        title="Open to edit"
                      >
                        <Chip variant={entry.kind === "Note" ? "default" : "info"}>
                          {entry.kind ? KIND_LABEL[entry.kind] : "Record"}
                        </Chip>
                        <span className="min-w-0 text-rs-off-white text-[13.5px] truncate">{entry.summary}</span>
                        <span className="text-rs-light-gray text-xs whitespace-nowrap">
                          {recordedLabel(entry.recordedUtc)}
                          {entry.enteredByDisplayName ? ` · ${entry.enteredByDisplayName}` : ""}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          />
        </div>
      )}
    </details>
  );
}
